import React, { useState } from 'react'
import { OutputMapEditor } from './OutputMapEditor'
import { InputMapEditor } from './InputMapEditor'

/**
 * Per-step config panel for a chain step (collapsible, same pattern as NodeEditor).
 *
 * Props:
 *   step               — current step object { guid, statePolicy, outputMap, inputMap }
 *   index              — step index in the chain
 *   simulation         — resolved simulation object (or null if GUID not found)
 *   coursenum           — for building edit links
 *   accumulatedVarNames — variable names from all previous steps' outputMaps
 *   onUpdate(index, newStep)
 *   onRemove(index)
 *   onMoveUp()
 *   onMoveDown()
 *   isFirst
 *   isLast
 */

const PLUGIN_COLORS = {
  'spreadsheet':      { bg: '#e3f2fd', text: '#1565c0', label: 'Spreadsheet' },
  'statement-table':  { bg: '#e8f5e9', text: '#2e7d32', label: 'Statement Table' },
  'interactive-path': { bg: '#fff3e0', text: '#e65100', label: 'Interactive Path' },
  'intro-card':       { bg: '#f3e5f5', text: '#6a1b9a', label: 'Intro Card' },
  'sorting':          { bg: '#fce4ec', text: '#c62828', label: 'Sorting' },
}

const SOURCE_HINTS = {
  'spreadsheet':      { hint: 'cellValues', placeholder: 'cellValues.B2' },
  'statement-table':  { hint: 'cellValues', placeholder: 'cellValues.B2' },
  'interactive-path': { hint: 'selections', placeholder: 'selections.interest_rate' },
  'sorting':          { hint: 'buckets', placeholder: 'buckets.process' },
  'intro-card':       { hint: '', placeholder: '' },
}

const removeBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#dc3545',
  cursor: 'pointer',
  fontSize: '13px',
  padding: '0',
}

export const PluginBadge = ({ plugin }) => {
  const colors = PLUGIN_COLORS[plugin] || { bg: '#e9ecef', text: '#495057', label: plugin || 'unknown' }
  return (
    <span style={{
      fontSize: '10px',
      fontWeight: 600,
      color: colors.text,
      background: colors.bg,
      borderRadius: '3px',
      padding: '1px 6px',
      flexShrink: 0,
      whiteSpace: 'nowrap',
    }}>
      {colors.label}
    </span>
  )
}

export const ChainStepEditor = ({
  step, index, simulation, coursenum, accumulatedVarNames,
  onUpdate, onRemove, onMoveUp, onMoveDown, isFirst, isLast,
}) => {
  const [collapsed, setCollapsed] = useState(false)

  const plugin = simulation ? simulation.plugin : null
  const title = simulation ? simulation.title : step.guid
  const colors = PLUGIN_COLORS[plugin] || { bg: '#e9ecef', text: '#495057' }

  const updateField = (field, value) => {
    onUpdate(index, { ...step, [field]: value })
  }

  const sourceInfo = SOURCE_HINTS[plugin] || { hint: '', placeholder: '' }

  return (
    <div
      data-chain-step-index={index}
      style={{
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        marginBottom: '8px',
        background: '#fff',
        borderLeft: `3px solid ${colors.bg === '#e9ecef' ? '#4285f4' : colors.text}`,
      }}
    >
      {/* Header bar — always visible */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          background: '#f8f9fa',
          borderBottom: collapsed ? 'none' : '1px solid #dee2e6',
          cursor: 'pointer',
          borderRadius: collapsed ? '0 4px 4px 0' : '0 4px 0 0',
        }}
        onClick={() => setCollapsed(!collapsed)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
          <span style={{ color: '#868e96', fontSize: '12px', flexShrink: 0 }}>
            {collapsed ? '\u25B6' : '\u25BC'}
          </span>
          <span style={{
            fontWeight: 700,
            fontSize: '13px',
            color: '#495057',
            background: '#e9ecef',
            borderRadius: '3px',
            padding: '1px 6px',
            flexShrink: 0,
          }}>
            {index + 1}
          </span>
          <span style={{ fontWeight: 600, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {title}
          </span>
          {plugin && <PluginBadge plugin={plugin} />}
          {!simulation && (
            <span style={{ fontSize: '11px', color: '#dc3545', flexShrink: 0 }}>
              GUID not found
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className="btn btn-myed btn-default btn-default-myed btn-sm"
            style={{ padding: '1px 6px', fontSize: '11px', opacity: isFirst ? 0.4 : 1 }}
            title="Move up"
          >&uarr;</button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className="btn btn-myed btn-default btn-default-myed btn-sm"
            style={{ padding: '1px 6px', fontSize: '11px', opacity: isLast ? 0.4 : 1 }}
            title="Move down"
          >&darr;</button>
          <button type="button" onClick={() => onRemove(index)} style={removeBtnStyle}>&times;</button>
        </div>
      </div>

      {/* Body — collapsible */}
      {!collapsed && (
        <div style={{ padding: '12px' }}>

          {/* Section A: Simulation Info */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '80px 1fr',
              gap: '4px 12px',
              fontSize: '13px',
              alignItems: 'center',
            }}>
              <span style={{ fontWeight: 600, color: '#6c757d' }}>Title</span>
              <span>{simulation ? simulation.title : '—'}</span>
              <span style={{ fontWeight: 600, color: '#6c757d' }}>Plugin</span>
              <span>{plugin ? <PluginBadge plugin={plugin} /> : '—'}</span>
              <span style={{ fontWeight: 600, color: '#6c757d' }}>GUID</span>
              <code style={{ fontSize: '12px', color: '#495057' }}>{step.guid}</code>
              <span style={{ fontWeight: 600, color: '#6c757d' }}>Points</span>
              <span style={{ fontSize: '13px', color: '#495057' }}>
                {simulation && simulation.tasks
                  ? (() => {
                      const total = (simulation.tasks || []).reduce((sum, t) => sum + (t.points || 0), 0)
                      const count = (simulation.tasks || []).length
                      return `${total} (${count} task${count !== 1 ? 's' : ''})`
                    })()
                  : '—'}
              </span>
            </div>
            {simulation && (
              <a
                href={`/instructor/simulations/${coursenum}/${simulation.pk}/`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '12px', marginTop: '6px', display: 'inline-block' }}
              >
                Edit Simulation &rarr;
              </a>
            )}
            {!simulation && (
              <div style={{
                marginTop: '6px',
                padding: '6px 10px',
                background: '#f8d7da',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#721c24',
              }}>
                No simulation found with GUID &quot;{step.guid}&quot; in this course.
              </div>
            )}
          </div>

          {/* Section B: State Policy */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px', display: 'block' }}>
              State Policy
            </label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['consume', 'passthrough'].map((policy) => (
                <button
                  key={policy}
                  type="button"
                  className={`btn btn-myed btn-sm ${(step.statePolicy || 'consume') === policy ? 'btn-primary btn-primary-myed' : 'btn-default btn-default-myed'}`}
                  onClick={() => updateField('statePolicy', policy)}
                >
                  {policy.charAt(0).toUpperCase() + policy.slice(1)}
                </button>
              ))}
            </div>
            <p style={{ fontSize: '11px', color: '#6c757d', margin: '4px 0 0 0' }}>
              {(step.statePolicy || 'consume') === 'consume'
                ? 'Consume = this simulation uses incoming state and produces new output.'
                : 'Passthrough = state flows through unchanged.'}
            </p>
          </div>

          {/* Section B2: Auto-advance */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
              <input
                type="checkbox"
                checked={!!step.autoAdvance}
                onChange={(e) => updateField('autoAdvance', e.target.checked || undefined)}
              />
              <span style={{ fontWeight: 600 }}>Auto-advance on completion</span>
            </label>
            <p style={{ fontSize: '11px', color: '#6c757d', margin: '4px 0 0 0' }}>
              Skip the &ldquo;Continue&rdquo; button and advance to the next simulation immediately when all tasks complete.
            </p>
          </div>

          {/* Section C: Output Map */}
          <div style={{ marginBottom: '16px' }}>
            <OutputMapEditor
              outputMap={step.outputMap || []}
              onChange={(newMap) => updateField('outputMap', newMap.length > 0 ? newMap : undefined)}
              sourceHint={sourceInfo.hint}
              sourcePlaceholder={sourceInfo.placeholder}
            />
          </div>

          {/* Section D: Input Map (only for steps after the first) */}
          {!isFirst && (
            <div>
              <InputMapEditor
                inputMap={step.inputMap || null}
                onChange={(newMap) => updateField('inputMap', newMap || undefined)}
                availableVarNames={accumulatedVarNames}
                pluginType={plugin || ''}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
